/**
 * The contents of one result row: a record with its status, value and when,
 * a campaign with whether it is running, a template with its subject.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Badge, Group, Text } from '@mantine/core';
import { IconUserCircle } from '@tabler/icons-react';
import type { components } from '../../api/schema';
import { TOKENS, formatGBP, formatNumber } from '../../theme';
import { relativeTime, titleCase } from '../../ui/format';
import { useVocab } from '../../vocab';
import type { Command } from '../commands';
import { ScoreBadge } from '../../pages/scoring/scoringShared';
import { showsRowType, useStreamOf } from '../../pages/streams/streamShared';

type LeadRow = components['schemas']['LeadSummary'];

export function leadName(l: LeadRow): string {
  return l.fullname || [l.firstname, l.lastname].filter(Boolean).join(' ') || l.email || l.phone1 || l.id;
}

export function StatusPill({ lead }: { lead: LeadRow }) {
  const v = useVocab();
  const status = lead.delivery_status || lead.status;
  const label = lead.sold ? v.p.Sold : titleCase(status || 'unknown');
  const color = lead.sold ? 'chartGreen' : ['rejected', 'error'].includes(status) ? 'chartRed' : 'gray';
  return <Badge size="xs" variant="light" color={color} style={{ flexShrink: 0 }}>{label}</Badge>;
}

const line2: CSSProperties = {
  fontSize: 12, color: TOKENS.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

export function LeadRowContent({ lead }: { lead: LeadRow }) {
  // "Energy Premium · lead → Acme": the stream, its row type where it is
  // worth saying, and who bought it.
  const stream = useStreamOf()(lead.campaign_id);
  const where = stream && showsRowType(stream.rowType)
    ? `${lead.campaign_name} · ${stream.label}`
    : lead.campaign_name;
  const bits = [where, lead.buyer_name ? `→ ${lead.buyer_name}` : ''].filter(Boolean);
  return (
    <Group gap={10} wrap="nowrap" style={{ width: '100%', minWidth: 0 }}>
      <IconUserCircle size={18} stroke={1.6} style={{ flexShrink: 0, opacity: 0.7 }} />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate data-testid="cmdk-lead-name">{leadName(lead)}</Text>
          <StatusPill lead={lead} />
          {lead.score != null && <ScoreBadge score={lead.score} band={lead.score_band} size="xs" style={{ flexShrink: 0 }} />}
        </Group>
        <div style={line2}>{bits.join(' ')}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <Text size="sm" fw={lead.revenue > 0 ? 700 : 400} c={lead.revenue > 0 ? undefined : 'dimmed'}>
          {formatGBP(lead.revenue)}
        </Text>
        <div style={{ fontSize: 11, color: TOKENS.textMuted }}>{relativeTime(lead.created_at)}</div>
      </div>
    </Group>
  );
}

export function CommandRowContent({ cmd, right }: { cmd: Command; right?: ReactNode }) {
  const Icon = cmd.icon;
  const m = cmd.meta;
  let detail: string | undefined = cmd.description;
  let badge: ReactNode = null;
  let aside: ReactNode = right ?? null;
  if (m?.kind === 'campaign') {
    badge = (
      <Badge size="xs" variant="light" color={m.active ? 'chartGreen' : 'gray'} style={{ flexShrink: 0 }}>
        {m.active ? 'Active' : 'Paused'}
      </Badge>
    );
    if (m.today !== null) aside = <Text size="xs" c="dimmed">{`${formatNumber(m.today)} today`}</Text>;
  } else if (m?.kind === 'template') {
    detail = m.subject || undefined;
    aside = <Text size="xs" c="dimmed">{`Edited ${relativeTime(m.updatedAt)}`}</Text>;
  } else if (m?.kind === 'segment' && m.count !== null) {
    aside = <Text size="xs" c="dimmed" data-testid="cmdk-segment-count">{formatNumber(m.count)}</Text>;
  }
  return (
    <Group gap={10} wrap="nowrap" style={{ width: '100%', minWidth: 0 }}>
      <Icon size={18} stroke={1.6} style={{ flexShrink: 0, opacity: 0.7 }} />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="sm" truncate data-testid="cmdk-label">{cmd.label}</Text>
          {badge}
        </Group>
        {detail && <div style={line2}>{detail}</div>}
      </div>
      {aside && <div style={{ flexShrink: 0 }}>{aside}</div>}
    </Group>
  );
}
